import { Route, Routes } from 'react-router-dom';

import Layout from '@/Pages/Layout';
import { PermissionRoute } from '@/components/PermissionRoute';

import Login from '@/Pages/Auth/Login';
import Register from '@/Pages/Auth/Register';
import ForgotPassword from '@/Pages/Auth/ForgotPassword';
import Overview from '@/Pages/Overview/Overview';
import FloorMap from '@/Pages/Spaces/FloorMap';
import KidsStation from '@/Pages/Spaces/KidsStation';
import Orders from '@/Pages/Operations/Orders';
import Kitchen from '@/Pages/Operations/Kitchen';
import WaiterStation from '@/Pages/Operations/WaiterStation';
import Inventory from '@/Pages/Operations/Inventory';
import Reservations from '@/Pages/Operations/Reservations';
import Procurement from '@/Pages/Operations/Procurement';
import Recipes from '@/Pages/Operations/Recipes';
import ServiceRequests from '@/Pages/Operations/ServiceRequests';
import Delivery from '@/Pages/Delivery/Delivery';
import Fleet from '@/Pages/Fleet/Fleet';
import Integrations from '@/Pages/Integrations/Integrations';
import DeliveryAggregators from '@/Pages/DeliveryAggregators/DeliveryAggregators';
import Fiscal from '@/Pages/Fiscal/Fiscal';
import Accounting from '@/Pages/Accounting/Accounting';
import CentralKitchen from '@/Pages/CentralKitchen/CentralKitchen';
import Approvals from '@/Pages/Platform/Approvals';
import AuditLog from '@/Pages/Platform/AuditLog';
import Checks from '@/Pages/Billing/Checks';
import Wallet from '@/Pages/Billing/Wallet';
// Aliased: `Register` above is the public sign-up page. The till is a
// station screen, and this names it like the other three.
import RegisterStation from '@/Pages/Billing/Register';
import Catalog from '@/Pages/Catalog/Catalog';
import MenuGovernance from '@/Pages/MenuGovernance/MenuGovernance';
import Brands from '@/Pages/Brands/Brands';
import BrandDetail from '@/Pages/Brands/BrandDetail';
import Customers from '@/Pages/CRM/Customers';
import Risk from '@/Pages/Risk/Risk';
import CallCenter from '@/Pages/CallCenter/CallCenter';
import Storefront from '@/Pages/Storefront/Storefront';
import Workforce from '@/Pages/Workforce/Workforce';
import TimeClock from '@/Pages/Workforce/TimeClock';
import Messaging from '@/Pages/Messaging/Messaging';
import Reports from '@/Pages/Reports/Reports';
import Copilot from '@/Pages/Copilot/Copilot';
import Settings from '@/Pages/Settings/Settings';
import SetupWizard from '@/Pages/Setup/SetupWizard';
import { SETUP_FIX_PERMISSIONS } from '@/Pages/Setup/useSetupStatus';
import Imports from '@/Pages/Imports/Imports';
import ForbiddenPage from '@/Pages/ForbiddenPage';
import NotFoundPage from '@/Pages/NotFoundPage';

/**
 * Every route is permission-gated (§24). The keys here mirror the ones the API
 * enforces, so hiding a route and rejecting the request stay in sync.
 */
export default function AppRoutes() {
  return (
    <Routes>
      {/*
          THE PUBLIC ROUTES. All three sit OUTSIDE the `<PermissionRoute />`
          element below, and that is the whole of the `/register` fix.

          `PermissionRoute` redirects an unauthenticated visitor to `/login`, and
          the `*` catch-all is nested INSIDE it — so a path with no route of its
          own never reached `NotFoundPage`, it reached the gate and bounced. That
          is why `dashboard.tarabeeza.com/register` answered with a login form:
          not a redirect anybody wrote, but the absence of a route.

          A registration page inside the gate is a contradiction — it exists to
          create the account the gate is asking for — so these are siblings of
          the gate, not children of it. `/forgot-password` likewise: the one
          person who needs it is by definition unable to sign in.
      */}
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />

      <Route element={<PermissionRoute />}>
        <Route element={<Layout />}>
          <Route index element={<Overview />} />

          {/*
              Every `:tab` below is a tabbed screen whose open tab is its URL.
              A tab is a place — it gets linked, bookmarked and sent to people —
              and it used to be `useState`, so none of that worked and the back
              button left the screen instead of returning to the previous tab.
              `Pages/routedTabs.tsx` holds the rule all of them share, and
              `Pages/tabRoutes.ts` the list of what each one answers on. The bare
              path stays: it redirects to the first tab the actor may see.
          */}
          <Route element={<PermissionRoute permissions={['floor_map.view']} />}>
            <Route path="floor-map" element={<FloorMap />} />
            <Route path="floor-map/:tab" element={<FloorMap />} />
          </Route>

          {/* §11 — the kids desk is a station screen, opened once and left
              running, which is why it is a route rather than a dialog on the
              floor map. Gated on `children.view`, which is what the board IS: a
              list of children and the adults who may collect them. The timer
              keys are a waiter's too and would have let the floor in here.
              `resources.view` and `resource_sessions.view` are needed as well,
              and the screen names them rather than 403ing, because an attendant
              standing at a desk needs to know which key to ask a manager for. */}
          <Route element={<PermissionRoute permissions={['children.view']} />}>
            <Route path="kids" element={<KidsStation />} />
          </Route>

          {/* The pass is `GET /orders` grouped by table, so it is the order
              list's own key — and the same gate as the rail, because a nav row
              that leads to a 403 is worse than no row. The call queue beside it
              needs `service_requests.view`; the screen leaves that half out for
              whoever lacks it rather than refusing the whole station. */}
          <Route element={<PermissionRoute permissions={['orders.view']} />}>
            <Route path="orders" element={<Orders />} />
            <Route path="orders/:tab" element={<Orders />} />
            <Route path="waiter-station" element={<WaiterStation />} />
          </Route>

          <Route element={<PermissionRoute permissions={['kitchen.view']} />}>
            <Route path="kitchen" element={<Kitchen />} />
          </Route>

          {/* §8 / §17 — the register, so a venue can be run entirely from the
              browser: order entry the dashboard had nowhere at all, plus the
              drawer and the settle surface it already had. `orders.create` and
              not `checks.view`, because taking an order is what this screen is
              for and it is the key a CASHIER holds; the bill it opens is
              `CheckDetail`, which asks for its own keys.

              `/till` and not `/register`: that path is the public sign-up page,
              which sits outside the gate above, and a second route on it would
              shadow one of them. "Till" is also what the billing screens call
              this thing in their own prose. */}
          <Route element={<PermissionRoute permissions={['orders.create']} />}>
            <Route path="till" element={<RegisterStation />} />
          </Route>

          <Route element={<PermissionRoute permissions={['checks.view', 'payments.view']} />}>
            <Route path="checks" element={<Checks />} />
          </Route>

          {/* ADR-0012 — gift cards and store credit. `wallet.view`, matching what
              `WalletController` requires on every read route; every mutating
              control on the screen is gated again on its own key (issue, reload,
              adjust, refund, close), because the backend deliberately did not
              collapse those into one `wallet.manage`. */}
          <Route element={<PermissionRoute permissions={['wallet.view']} />}>
            <Route path="wallet" element={<Wallet />} />
          </Route>

          <Route element={<PermissionRoute permissions={['menu.view']} />}>
            <Route path="catalog" element={<Catalog />} />
            <Route path="catalog/:tab" element={<Catalog />} />
          </Route>

          {/* §20 — central menu governance (ADR-0011). A layer above the
              catalogue rather than a Catalog tab, and gated on its own key
              rather than `menu.view`: every branch manager holds all of
              `menu.*` because pricing their own venue is ordinary work, and
              folding this in would hand one branch the power to reprice the
              other thirty-nine — the exact thing this module exists to
              prevent (`menu_governance.permissions.spec.ts`). */}
          <Route element={<PermissionRoute permissions={['menu_governance.view']} />}>
            <Route path="menu-governance" element={<MenuGovernance />} />
            <Route path="menu-governance/:tab" element={<MenuGovernance />} />
          </Route>

          {/* ADR-0023 §19 — franchise and brand governance: which branches
              group under a brand, its standards, consolidated analytics,
              royalty schemes/statements and onboarding templates. `brands.view`
              matches the route both `BrandsController` and `RoyaltyController`
              require on every read; every mutating control inside gates itself
              again on its own key (`brands.manage`, `.manage_standards`,
              `.analytics`, `.onboard`, `royalty.configure`) rather than
              collapsing them — a franchisee branch manager reads its own
              brand's standards and royalty figures without holding the
              authority to redraw brand membership or change a rate
              (`roles.ts`'s `BRANCH_MANAGER` vs `BRAND_MANAGER`). */}
          <Route element={<PermissionRoute permissions={['brands.view']} />}>
            <Route path="brands" element={<Brands />} />
            <Route path="brands/:brandId" element={<BrandDetail />} />
            <Route path="brands/:brandId/:tab" element={<BrandDetail />} />
          </Route>

          <Route element={<PermissionRoute permissions={['inventory.view']} />}>
            <Route path="inventory" element={<Inventory />} />
            <Route path="inventory/:tab" element={<Inventory />} />
            <Route path="procurement" element={<Procurement />} />
            <Route path="procurement/:tab" element={<Procurement />} />
            <Route path="recipes" element={<Recipes />} />
          </Route>

          {/* C-13 / §24.5 — the route gate is the queue's *read* key, matching
              what the API requires to list it. Whether a row can actually be
              signed is a per-`PolicyKind` question the screen asks per row. */}
          <Route element={<PermissionRoute permissions={['approvals.view']} />}>
            <Route path="approvals" element={<Approvals />} />
          </Route>

          <Route element={<PermissionRoute permissions={['service_requests.view']} />}>
            <Route path="service-requests" element={<ServiceRequests />} />
          </Route>

          {/* §16 own delivery & driver operations (ADR-0029) — the dispatch
              board, driver cash reconciliation, the driver roster, zones and
              fees, the location-tracking policy, and delivery analytics.
              `canAny` over every tab's own read key, matching the
              Fleet/Integrations/CentralKitchen/Risk/Workforce I-07 shape: a
              role holding only `driver_shifts.view` (a DRIVER, reconciling
              nothing but their own shift) still reaches the screen and lands
              on the one tab they hold, rather than a 403 before the shell can
              show them anything. */}
          <Route
            element={
              <PermissionRoute
                permissions={[
                  'deliveries.view',
                  'driver_shifts.view',
                  'drivers.view',
                  'delivery_zones.view',
                  'delivery_location.configure',
                ]}
              />
            }
          >
            <Route path="delivery" element={<Delivery />} />
            <Route path="delivery/:tab" element={<Delivery />} />
          </Route>

          {/* §11 / ADR-0009 — the device and printer fleet. `canAny` over the four
              read keys the API enforces, and the same set the rail shows the row
              on, because the shell renders only the tabs the actor holds: a
              cashier with `print_jobs.view` and nothing else belongs on the
              spool, and gating the route on `devices.view` would 403 them before
              the shell could show it to them. That is the I-07 failure the
              Settings gate below already documents. */}
          <Route
            element={
              <PermissionRoute
                permissions={[
                  'print_jobs.view',
                  'devices.view',
                  'printers.view',
                  'print_bridges.view',
                ]}
              />
            }
          >
            <Route path="fleet" element={<Fleet />} />
            <Route path="fleet/:tab" element={<Fleet />} />
          </Route>

          {/* §2 / §3 — the integration substrate (ADR-0002), outbound webhooks
              (ADR-0004) and ADR-0003 / ADR-0027's `api_clients` (the venue's
              own credentials for `/public/v1`). `canAny` over all three
              modules' own read keys, matching the rail: an integration
              engineer holding only `webhooks.view` — or only
              `api_clients.view` — must reach this screen and land on the tab
              they hold, not be 403'd before the shell can show it to them —
              the same I-07 shape the Settings and Fleet gates document. Every
              mutating control inside gates itself again on its own key
              (`integrations.install`, `.configure`, `.credentials.rotate`,
              `.sync`, `.replay`, `.uninstall`, `webhooks.manage`,
              `.rotate_secret`, `.replay`, `api_clients.create`, `.update`,
              `.revoke`) — this route only decides who may look. */}
          <Route
            element={
              <PermissionRoute
                permissions={['integrations.view', 'webhooks.view', 'api_clients.view']}
              />
            }
          >
            <Route path="integrations" element={<Integrations />} />
            <Route path="integrations/:tab" element={<Integrations />} />
          </Route>

          {/* ADR-0028 Part B — delivery aggregators. `delivery_aggregators.view`
              matches the route: BRANCH_MANAGER and SHIFT_MANAGER both hold it
              (and `accept_reject`/`cancel` beside it), so a shift manager
              answering the SLA clock overnight is never 403'd at the door.
              Every mutating control inside gates itself again on its own key
              (`.accept_reject`, `.cancel`, `.manage_mappings`) — this route
              only decides who may look. */}
          <Route element={<PermissionRoute permissions={['delivery_aggregators.view']} />}>
            <Route path="delivery-aggregators" element={<DeliveryAggregators />} />
            <Route path="delivery-aggregators/:tab" element={<DeliveryAggregators />} />
          </Route>

          {/* §5 / ADR-0005 — Egypt fiscal compliance. `fiscal.view` matches
              the route both `FiscalController` and `FiscalDocumentsController`
              enforce on every read; every mutating control inside gates
              itself again on its own key (`fiscal.configure`, `.credentials`,
              `.retry`, `.cancel`, `.resolve`) rather than collapsing them,
              because the person who maintains a menu's EGS codes is not
              automatically the person who should hold the taxpayer secret. */}
          <Route element={<PermissionRoute permissions={['fiscal.view']} />}>
            <Route path="fiscal" element={<Fiscal />} />
            <Route path="fiscal/:tab" element={<Fiscal />} />
          </Route>

          {/* ADR-0028 Part A — the accounting posting ledger. `accounting.view`
              matches what `AccountingPostingsController` enforces on both its
              read routes; `accounting.post` (build a posting for a check by
              hand) and `accounting.reverse` are gated again at the control,
              not the route, because a bookkeeper who may read the ledger is
              not automatically the one trusted to write a reversal into a
              real external one. Not tabbed like Fiscal beside it: accounting
              has no configuration aggregate of its own to be a second tab —
              the installation's `config` JSON, edited from Integrations, is
              the whole of it (see `Pages/Accounting/Accounting.tsx`'s own
              header for why a second, mostly-empty tab was not built). */}
          <Route element={<PermissionRoute permissions={['accounting.view']} />}>
            <Route path="accounting" element={<Accounting />} />
          </Route>

          {/* §18 / ADR-0015 — the commissary: batches, transfers with a
              middle, branch requisitions and replenishment. `canAny` over the
              four modules' own read keys, matching the rail — a commissary
              clerk holding only `stock_transfers.view` still reaches the
              screen and lands on the one tab they hold, the same I-07 shape
              Fleet and Integrations already document. */}
          <Route
            element={
              <PermissionRoute
                permissions={[
                  'stock_transfers.view',
                  'production.view',
                  'requisitions.view',
                  'replenishment.view',
                ]}
              />
            }
          >
            <Route path="central-kitchen" element={<CentralKitchen />} />
            <Route path="central-kitchen/:tab" element={<CentralKitchen />} />
          </Route>

          {/* §34 — read-only audit trail, on the same key as the API. */}
          <Route element={<PermissionRoute permissions={['reports.view']} />}>
            <Route path="audit" element={<AuditLog />} />
          </Route>

          <Route element={<PermissionRoute permissions={['reservations.view', 'waitlist.view']} />}>
            <Route path="reservations" element={<Reservations />} />
            <Route path="reservations/:tab" element={<Reservations />} />
          </Route>

          <Route element={<PermissionRoute permissions={['customers.view']} />}>
            <Route path="customers" element={<Customers />} />
            <Route path="customers/:tab" element={<Customers />} />
          </Route>

          {/* §22 / ADR-0017 — fraud, loss prevention and anomaly detection. The
              route gate is `canAny` over every key any tab needs, matching the
              Fleet/Integrations/Settings shape above: `risk.review` alone opens
              the queue, `risk.configure` alone opens Rules and Reviewers, and
              `RISK_TABS` in `tabRoutes.ts` is what stops either holder landing
              on a tab that answers 403. */}
          <Route element={<PermissionRoute permissions={['risk.review', 'risk.configure']} />}>
            <Route path="risk" element={<Risk />} />
            <Route path="risk/:tab" element={<Risk />} />
          </Route>

          {/* ADR-0021 §15 — the call-centre desk. `orders.create` matches
              Till's own reasoning: a console with nothing gated on the key
              that lets someone actually place an order is a screen full of
              buttons that 403. Phone lookup inside gates itself again on the
              PII-specific `customers.search_phone` (ADR-0021 §2.6) and is
              refused with an explanation, not a blank screen, when that key
              is absent. */}
          <Route element={<PermissionRoute permissions={['orders.create']} />}>
            <Route path="call-center" element={<CallCenter />} />
          </Route>

          {/* §4 / ADR-0008 — the time clock. A station screen like the till: opened
              once at the start of a shift and left running. `attendance.punch` is
              "clock MYSELF in and out", and every floor role holds it — a waiter
              who cannot clock in is a waiter who is not paid. */}
          <Route element={<PermissionRoute permissions={['attendance.punch']} />}>
            <Route path="time-clock" element={<TimeClock />} />
          </Route>

          {/* The workforce management screen — staff records, timesheets,
              exceptions, the attendance policy and scheduling (roster, shift
              templates, availability, swaps, labour, and — `scheduling.view_own`
              — the self-service "My schedule" tab). `canAny` over every tab's
              own key, matching the Fleet/Integrations/Risk shape: an accountant
              holding only `attendance.view` (never `employees.*`, `scheduling.*`
              or `compensation.*` — ADR-0008 §4) still has somewhere to land, on
              Timesheets rather than a 403 Employees would give them; a Shift
              Manager holding only `scheduling.*` lands on Roster rather than a
              403 Employees would give them; and a waiter or cashier holding only
              `scheduling.view_own` (every floor role — `attendance.punch` gates
              the separate `/time-clock` station, not this screen) lands on My
              Schedule rather than having no door into their own roster at all. */}
          <Route
            element={
              <PermissionRoute
                permissions={[
                  'employees.view',
                  'attendance.view',
                  'scheduling.view',
                  'scheduling.view_own',
                ]}
              />
            }
          >
            <Route path="workforce" element={<Workforce />} />
            <Route path="workforce/:tab" element={<Workforce />} />
          </Route>

          {/* ADR-0016 §8 — the branded storefront console: branding, the
              custom domain and its DNS verification state, branch
              publishing, opening hours, SEO and abandoned-cart telemetry.
              `storefront.view` is the read floor every tab needs;
              `storefront.manage_domains` is its own key (see `tabRoutes.ts`)
              because a hostname decides which tenant an unauthenticated
              request reads, a different blast radius from editing copy. */}
          <Route
            element={
              <PermissionRoute permissions={['storefront.view', 'storefront.manage_domains']} />
            }
          >
            <Route path="storefront" element={<Storefront />} />
            <Route path="storefront/:tab" element={<Storefront />} />
          </Route>

          {/* ADR-0026 §9 — templates, campaigns, and the tenant-wide quiet
              hours and fatigue-cap controls. `canAny` over the read floor of
              the two families that actually gate the module's data
              (`message_templates.view`, `campaigns.view`); `message_templates.*`
              and `campaigns.*` are mapped to no system role but Tenant
              Owner/Admin by design (see `tabRoutes.ts`), so this route is
              reachable to the same actors the storefront one above is. */}
          <Route
            element={
              <PermissionRoute permissions={['message_templates.view', 'campaigns.view']} />
            }
          >
            <Route path="messaging" element={<Messaging />} />
            <Route path="messaging/:tab" element={<Messaging />} />
          </Route>

          {/* ADR-0031 — forecasting is a Reports tab (`forecasting.view`), not a rail
              row of its own. `canAny`, matching the Fleet/Integrations/Risk shape:
              `INVENTORY_MANAGER` and `PURCHASING` hold `forecasting.view` and also
              happen to hold `reports.view` today, but a custom role granted only
              the former must still reach the screen and land on its one tab,
              rather than 403 at the route before `REPORTS_TABS` can filter it. */}
          <Route element={<PermissionRoute permissions={['reports.view', 'forecasting.view']} />}>
            <Route path="reports" element={<Reports />} />
            <Route path="reports/:tab" element={<Reports />} />
          </Route>

          {/* ADR-0032 — the governed operations copilot. `copilot.use` alone: what
              a question may actually READ is decided per-call by `ReportAccess`
              inside the semantic engine (the same authority the report builder
              runs under), so holding this key grants no reach beyond what
              `reports.view`/`.view_cost` etc. already permit — it only turns the
              feature itself on, which is why it defaults to TENANT_OWNER/ADMIN. */}
          <Route element={<PermissionRoute permissions={['copilot.use']} />}>
            <Route path="copilot" element={<Copilot />} />
          </Route>

          {/* I-07 — `PermissionRoute` is `canAny`, and the Settings shell renders
              only the tabs the actor holds. Gating on `settings.view` alone 403'd
              an RBAC administrator who holds `users.view` and nothing else, at
              the route, before the shell could show them their one tab. */}
          <Route
            element={
              <PermissionRoute
                permissions={[
                  'settings.view',
                  'users.view',
                  'roles.view',
                  'devices.view',
                  // The pricing tab is gated on `resources.view` and nothing
                  // else, so a floor manager holding only that key would have
                  // been 403'd at the route before the shell could show them
                  // their one tab — the exact I-07 failure the note above
                  // describes, in a new tab.
                  'resources.view',
                  // §13 / ADR-0018 — the Booking tab is gated on
                  // `booking_widget.view` alone, the same I-07 shape again: a
                  // host-desk lead who holds it and nothing else in this list
                  // must still land on the one tab that is theirs.
                  'booking_widget.view',
                  // ADR-0027 — the Marketplace tab. Neither key rides in on
                  // `settings.view`: a tenant that only installs someone
                  // else's app must still reach this screen and land on the
                  // one tab they hold, the same I-07 shape as
                  // `resources.view` above. (The developer portal's own
                  // `developer_apps.view` used to join this list too — it no
                  // longer gates anything reachable here, since registering
                  // and publishing an app moved to its own application.)
                  'marketplace.browse',
                  'marketplace.install',
                ]}
              />
            }
          >
            {/*
                The tab is a route segment, so each Settings tab is its own URL: back
                and forward work, a tab can be linked or bookmarked, and a reload
                lands where the operator was rather than on the first tab. `settings`
                with no segment redirects to the first tab the actor may actually
                see — not the same tab for everyone, since the list is filtered by
                permission.
            */}
            <Route path="settings" element={<Settings />} />
            <Route path="settings/:tab" element={<Settings />} />
          </Route>

          {/* C-15 — gated on the *write* keys, because the wizard exists to be
              acted on: someone who can only read it can do nothing but worry. The
              screen still shows every step's state to whoever gets here.

              The step is a route segment, like every other tab in this app, so a
              half-finished setup can be linked, bookmarked and resumed, and the
              back button walks back through the steps rather than off the screen.
              `/setup` with no segment lands on the first step the API still says
              is unfinished. */}
          <Route element={<PermissionRoute permissions={SETUP_FIX_PERMISSIONS} />}>
            <Route path="setup" element={<SetupWizard />} />
            <Route path="setup/:step" element={<SetupWizard />} />
          </Route>

          {/* ADR-0007 — the Migration Center: a mapping step, a dry run and an
              idempotent commit for menu items, customers, suppliers, inventory
              items and opening stock. `imports.view`, matching what every read
              route on `ImportsController` enforces; uploading and validating
              write nothing and need only `imports.create`, and landing a job
              needs `imports.commit` *plus* the target dataset's own write key
              (`menu.create`/`.update`, `customers.*`, `inventory.adjust`,
              `inventory.define`) — checked inside the screen, because the
              dataset is only known once a job is opened.

              No rail row. This is prepared-once, occasionally-repeated tooling
              — a venue arriving from another system, or someone re-importing a
              supplier list months later — not a station a shift stands at, so
              it does not compete with Fleet, Integrations and Fiscal for the
              64px-collapsed rail's one glyph per screen. It is reached from
              the Setup wizard instead, which is where a new venue already is
              when this question comes up. */}
          <Route element={<PermissionRoute permissions={['imports.view']} />}>
            <Route path="imports" element={<Imports />} />
          </Route>

          <Route path="403" element={<ForbiddenPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Route>
    </Routes>
  );
}
